'use client';

import { useState, useEffect, useRef } from 'react';
import { AdminModal } from '@/components/ui/Modal';
import { submitCorrection, uploadCorrectionAttachment, ATTACHMENT_MAX_SIZE, ATTACHMENT_ACCEPTED_TYPES } from '@/lib/db-corrections';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import type { IssueType } from '@/lib/db-corrections';

const DEDUP_STORAGE_KEY = 'almanak_reported_pages';

function getReportedPages(): string[] {
  try {
    const raw = localStorage.getItem(DEDUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function markPageReported(slug: string): void {
  try {
    const reported = getReportedPages();
    if (!reported.includes(slug)) {
      reported.push(slug);
      localStorage.setItem(DEDUP_STORAGE_KEY, JSON.stringify(reported));
    }
  } catch { /* localStorage unavailable */ }
}

function getEventIssueTypes(lang: string): { value: IssueType; label: string }[] {
  return [
    { value: 'wrong_date', label: uiFb('ui.wrong_dates', 'Wrong date(s)', lang) },
    { value: 'missing_event', label: uiFb('ui.missing_event', 'Missing event', lang) },
    { value: 'wrong_event_name', label: uiFb('ui.wrong_event_name', 'Wrong event name', lang) },
    { value: 'wrong_details', label: uiFb('ui.wrong_details', 'Wrong details', lang) },
    { value: 'duplicate_event', label: uiFb('ui.duplicate_event', 'Duplicate event', lang) },
    { value: 'other', label: uiFb('ui.other', 'Other', lang) },
  ];
}

function getPageIssueTypes(lang: string): { value: IssueType; label: string }[] {
  return [
    { value: 'wrong_details', label: uiFb('ui.wrong_details', 'Wrong details', lang) },
    { value: 'missing_translation', label: uiFb('ui.missing_translation', 'Missing translation', lang) },
    { value: 'broken_link', label: uiFb('ui.broken_link', 'Broken link', lang) },
    { value: 'other', label: uiFb('ui.other', 'Other', lang) },
  ];
}

interface ReportModalProps {
  pageSlug: string;
  entityId?: string;
  pageTitle: string;
  pageCountry?: string;
  pageType?: 'event' | 'page';
  onClose: () => void;
}

export function ReportModal({ pageSlug, entityId, pageTitle, pageCountry, pageType = 'event', onClose }: ReportModalProps) {
  const [issueType, setIssueType] = useState<IssueType | ''>('');
  const [correctValue, setCorrectValue] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);
  const [error, setError] = useState('');
  const lang = useCalendarStore((s) => s.lang);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setAlreadyReported(getReportedPages().includes(pageSlug));
    }
  }, [pageSlug]);

  // Clean up object URL on unmount or when attachment changes
  useEffect(() => {
    return () => {
      if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    };
  }, [attachmentPreview]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ATTACHMENT_ACCEPTED_TYPES.includes(file.type)) {
      setError(uiFb('ui.invalid_file_type', 'Invalid file type. Please use JPEG, PNG, WebP, or GIF.', lang));
      return;
    }
    if (file.size > ATTACHMENT_MAX_SIZE) {
      setError(uiFb('ui.file_too_large', 'File is too large. Maximum size is 5 MB.', lang));
      return;
    }

    setError('');
    if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    setAttachment(file);
    setAttachmentPreview(URL.createObjectURL(file));
  };

  const removeAttachment = () => {
    if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!issueType) {
      setError(uiFb('ui.please_select_issue', 'Please select an issue type', lang));
      return;
    }
    if (!correctValue.trim() && !description.trim()) {
      setError(uiFb('ui.please_provide_correction', 'Please provide a correction or description.', lang));
      return;
    }

    // Honeypot: silently fake success
    if (honeypot) {
      setSubmitted(true);
      setTimeout(onClose, 2000);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      let attachmentUrl: string | undefined;
      if (attachment) {
        attachmentUrl = await uploadCorrectionAttachment(attachment);
      }

      await submitCorrection({
        event_slug: pageSlug,
        event_id: entityId || undefined,
        event_title: pageTitle,
        event_country: pageCountry || '',
        issue_type: issueType,
        correct_value: correctValue || undefined,
        description: description || undefined,
        submitter_email: email || undefined,
        page_url: typeof window !== 'undefined' ? window.location.href : undefined,
        attachment_url: attachmentUrl,
      });
      window._paq?.push(['trackEvent', 'Report', 'Submit', pageSlug]);
      markPageReported(pageSlug);
      setSubmitted(true);
      setTimeout(onClose, 2000);
    } catch {
      setError(uiFb('ui.unable_to_submit', 'Unable to submit report. Please try again later.', lang));
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadyReported) {
    return (
      <AdminModal titleId="report-already-title" onClose={onClose}>
        <div className="py-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-semantic-info/15">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 8v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" stroke="var(--semantic-info)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 id="report-already-title" className="font-heading text-lg font-bold text-text-primary">
            {uiFb('ui.already_reported', 'Already reported', lang)}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {uiFb('ui.report_reviewing', "You've already submitted a report for this event. We're reviewing it.", lang)}
          </p>
        </div>
      </AdminModal>
    );
  }

  if (submitted) {
    return (
      <AdminModal titleId="report-success-title" onClose={onClose}>
        <div className="py-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-semantic-success/15">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 13l4 4L19 7" stroke="var(--semantic-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 id="report-success-title" className="font-heading text-lg font-bold text-text-primary">
            {uiFb('ui.thank_you', 'Thank you!', lang)}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {uiFb('ui.report_submitted', "Your report has been submitted. We'll review it shortly.", lang)}
          </p>
        </div>
      </AdminModal>
    );
  }

  const inputClasses = 'w-full content-card-elevated px-3 py-2 text-sm text-text-primary focus:border-brand/50 focus-visible:outline-2 focus-visible:outline-border-focus focus-visible:outline-offset-2';

  return (
    <AdminModal titleId="report-form-title" onClose={onClose}>
      <h2 id="report-form-title" className="mb-1 font-heading text-lg font-bold text-text-primary">
        {uiFb('ui.report_incorrect', 'Report incorrect data', lang)}
      </h2>
      <p className="mb-4 text-sm text-text-secondary">
        {uiFb('ui.help_improve', 'Help us improve by reporting an issue with', lang)} <span className="font-medium text-text-primary">{pageTitle}</span>
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="report-issue-type" className="mb-1 block text-xs text-text-secondary">
            {uiFb('ui.issue_type', 'Issue type', lang)} *
          </label>
          <select
            id="report-issue-type"
            value={issueType}
            onChange={(e) => setIssueType(e.target.value as IssueType)}
            className={inputClasses}
            required
          >
            <option value="">{uiFb('ui.select_issue', 'Select an issue...', lang)}</option>
            {(pageType === 'page' ? getPageIssueTypes(lang) : getEventIssueTypes(lang)).map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="report-correct-value" className="mb-1 block text-xs text-text-secondary">
            {uiFb('ui.what_should_it_be', 'What should it be?', lang)}
          </label>
          <input
            id="report-correct-value"
            type="text"
            value={correctValue}
            onChange={(e) => setCorrectValue(e.target.value)}
            className={inputClasses}
            placeholder={uiFb('ui.correction_placeholder', 'e.g. March 15 instead of March 14', lang)}
          />
        </div>

        <div>
          <label htmlFor="report-description" className="mb-1 block text-xs text-text-secondary">
            {uiFb('ui.additional_context', 'Additional context', lang)}
          </label>
          <textarea
            id="report-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={inputClasses}
            rows={3}
            placeholder={uiFb('ui.context_placeholder', 'Any extra details that might help us verify...', lang)}
          />
        </div>

        <div>
          <label htmlFor="report-email" className="mb-1 block text-xs text-text-secondary">
            {uiFb('ui.your_email', 'Your email (optional)', lang)}
          </label>
          <input
            id="report-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClasses}
            placeholder={uiFb('ui.email_placeholder', 'For follow-up if needed', lang)}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs text-text-secondary">
            {uiFb('ui.attach_screenshot', 'Attach screenshot (optional)', lang)}
          </label>
          {attachmentPreview ? (
            <div className="relative inline-block">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={attachmentPreview} alt="Preview" className="max-h-32 rounded-lg border border-border-subtle" />
              <button
                type="button"
                onClick={removeAttachment}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-surface-elevated text-text-secondary shadow-sm transition-colors hover:bg-semantic-error hover:text-white"
                aria-label={uiFb('ui.remove_attachment', 'Remove attachment', lang)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 rounded-lg border border-dashed border-border-subtle px-3 py-2 text-sm text-text-secondary transition-colors hover:border-brand/50 hover:text-text-primary"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {uiFb('ui.choose_image', 'Choose image...', lang)}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ATTACHMENT_ACCEPTED_TYPES.join(',')}
            onChange={handleFileSelect}
            className="hidden"
            aria-hidden="true"
          />
        </div>

        {/* Honeypot — hidden from real users */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <label htmlFor="report-website">Website</label>
          <input
            id="report-website"
            type="text"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {error && <p className="text-xs text-semantic-error">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-raised"
          >
            {uiFb('ui.cancel', 'Cancel', lang)}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-text-on-brand transition-colors hover:bg-brand/90 disabled:opacity-50"
          >
            {submitting ? uiFb('ui.submitting', 'Submitting...', lang) : uiFb('ui.submit_report', 'Submit Report', lang)}
          </button>
        </div>
      </form>
    </AdminModal>
  );
}
